/**
 * JoinPoolModal Component - Phase 2C-2
 * 
 * Confirmation modal for joining a pool.
 * Shows pool details and initial bankroll before confirming.
 */

import { Modal, Button } from '../common';
import { ModeBadge, StatusBadge } from '../common';
import { Currency } from '../common';
import { getDomainLabel, formatDate } from '../../utils';
import type { Pool } from '../../types';

interface JoinPoolModalProps {
  pool: Pool;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  error?: string | null;
}

export function JoinPoolModal({
  pool,
  isOpen,
  onClose,
  onConfirm,
  loading = false,
  error = null,
}: JoinPoolModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Join Pool">
      <div className="space-y-4">
        {/* Pool Summary */}
        <div className="bg-slate-50 rounded-lg p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-semibold text-slate-900">{pool.pool_name}</h3>
              <p className="text-sm text-slate-500 font-mono">{pool.pool_id}</p>
            </div>
            <StatusBadge status={pool.status} />
          </div>
          
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-slate-500">Domain:</span>
              <span className="ml-2 font-medium">{getDomainLabel(pool.domain)}</span>
            </div>
            <div>
              <span className="text-slate-500">Mode:</span>
              <span className="ml-2"><ModeBadge mode={pool.mode} size="sm" /></span>
            </div>
            <div>
              <span className="text-slate-500">Start:</span>
              <span className="ml-2 font-mono text-xs">{formatDate(pool.start_date)}</span>
            </div>
            <div>
              <span className="text-slate-500">End:</span>
              <span className="ml-2 font-mono text-xs">{formatDate(pool.end_date)}</span>
            </div>
          </div>
        </div>

        {/* Bankroll Info */}
        <div className="border border-slate-200 rounded-lg p-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="font-medium text-slate-900">Starting Bankroll</p>
              <p className="text-sm text-slate-500">
                This is your initial balance for making predictions
              </p>
            </div>
            <div className="text-2xl font-mono font-bold text-slate-900">
              <Currency value={pool.initial_bankroll} />
            </div>
          </div>
        </div>

        {/* Participants Info */}
        {pool.max_participants && (
          <div className="text-sm text-slate-600">
            <span className="font-medium">{pool.participant_count || 0}</span> of{' '}
            <span className="font-medium">{pool.max_participants}</span> seats filled
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Description */}
        {pool.description && (
          <p className="text-sm text-slate-600">{pool.description}</p>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={loading} className="flex-1">
            {loading ? 'Joining...' : 'Join Pool'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
